import { useCallback } from "react";
import { useAppDispatch, useAppSelector } from "../../../redux/store";
import {
    getAllServiceAreas,
    createServiceArea,
    updateServiceArea,
    deleteServiceArea,
    getServiceAreaById,
    ServiceAreaParams,
    CreateServiceAreaData
} from "../../../redux/Actions/ServiceAreaAction";
import { clearSelectedServiceArea, clearError } from "../../../redux/Slices/ServiceAreaSlice";
import { ServiceArea, ServiceAreaType } from "../models/ServiceArea.model";

export const useServiceArea = () => {
    const dispatch = useAppDispatch();
    const { serviceAreas, selectedServiceArea, loading, error } = useAppSelector((state) => state.serviceArea);

    // Get all service areas
    const fetchServiceAreas = useCallback(async (params: ServiceAreaParams) => {
        return await dispatch(getAllServiceAreas(params));
    }, [dispatch]);

    // Create new service area
    const createNewServiceArea = useCallback(async (data: CreateServiceAreaData) => {
        return await dispatch(createServiceArea({ data }));
    }, [dispatch]);

    // Update existing service area
    const updateExistingServiceArea = useCallback(async (id: string, data: CreateServiceAreaData) => {
        return await dispatch(updateServiceArea({ id, data }));
    }, [dispatch]);

    // Delete service area
    const deleteExistingServiceArea = useCallback(async (id: string) => {
        return await dispatch(deleteServiceArea({ id }));
    }, [dispatch]);

    // Get service area by ID
    const fetchServiceAreaById = useCallback(async (id: string) => {
        return await dispatch(getServiceAreaById({ id }));
    }, [dispatch]);

    // Clear selected service area
    const clearSelected = useCallback(() => {
        dispatch(clearSelectedServiceArea());
    }, [dispatch]);

    // Clear error
    const clearErrorState = useCallback(() => {
        dispatch(clearError());
    }, [dispatch]);

    // Helper function to get service area type display name
    const getServiceAreaTypeName = useCallback((typeId: number): string => {
        switch (typeId) {
            case ServiceAreaType.METROPOLITAN:
                return "Metropolitan Area";
            case ServiceAreaType.CITY:
                return "City";
            case ServiceAreaType.REGION:
                return "Region";
            case ServiceAreaType.PROVINCE:
                return "Province";
            default:
                return "Unknown";
        }
    }, []);

    // Helper function to filter service areas
    const filterServiceAreas = useCallback((searchQuery: string, selectedType: string) => {
        return serviceAreas.data.filter(area => {
            const matchesSearch = (area.name || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
                                 (area.province || "").toLowerCase().includes(searchQuery.toLowerCase());
            
            let matchesType = true;
            if (selectedType !== "all") {
                const areaTypeId = parseInt(area.type?.id?.toString() || area.type?.toString() || "0");
                const selectedTypeId = parseInt(selectedType);
                matchesType = areaTypeId === selectedTypeId;
            }
            
            return matchesSearch && matchesType;
        });
    }, [serviceAreas.data]);

    return {
        // State
        serviceAreas,
        selectedServiceArea,
        loading,
        error,
        
        // Actions
        fetchServiceAreas,
        createNewServiceArea,
        updateExistingServiceArea,
        deleteExistingServiceArea,
        fetchServiceAreaById,
        clearSelected,
        clearErrorState,
        
        // Helpers
        getServiceAreaTypeName,
        filterServiceAreas
    };
};
